/**
 * MasterDataCvbbTypeFormDialog — Create/Edit `congvan_bienban_type` (CVBB Type).
 * Compact Sales Contract style dialog. Single-column.
 *
 * PK `cvbb_id` là AUTO-INT (DB-generated) → create mode KHÔNG có field PK;
 * edit mode chỉ hiển thị cvbb_id ở dạng readonly.
 */
import {
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'

import { useSnackbar } from '@/hooks/useSnackbar'
import {
  useCreateMdCvbbTypeMutation,
  useUpdateMdCvbbTypeMutation,
} from '@/store/api/masterDataCrudApi'
import type { MasterDataCvbbType } from '@/types/api'
import { getErrorMessage } from '@/utils/error'

import { compactDialogContentSx, dialogHeaderBtnSx, dialogTitleActionsSx, dialogTitleSx, dialogTitleTextSx, roSx } from '../purchase/dialogStyle'

export interface Props {
  open: boolean
  mode: 'create' | 'edit'
  initialValue?: MasterDataCvbbType | null
  onClose: () => void
  onSaved?: () => void
}

// FormState KHÔNG chứa cvbb_id (auto-gen / readonly-display only).
type FormState = {
  cvbb_type: string
  cvbb_vn: string
  cvbb_eng: string
  note: string
}

const EMPTY: FormState = {
  cvbb_type: '',
  cvbb_vn: '',
  cvbb_eng: '',
  note: '',
}

export default function MasterDataCvbbTypeFormDialog({
  open,
  mode,
  initialValue,
  onClose,
  onSaved,
}: Props) {
  const snack = useSnackbar()
  const isEdit = mode === 'edit'

  const [createCvbbType, { isLoading: creating }] = useCreateMdCvbbTypeMutation()
  const [updateCvbbType, { isLoading: updating }] = useUpdateMdCvbbTypeMutation()
  const loading = creating || updating

  const [form, setForm] = useState<FormState>(EMPTY)

  useEffect(() => {
    if (!open) return
    if (isEdit && initialValue) {
      setForm({
        cvbb_type: initialValue.cvbb_type ?? '',
        cvbb_vn: initialValue.cvbb_vn ?? '',
        cvbb_eng: initialValue.cvbb_eng ?? '',
        note: initialValue.note ?? '',
      })
    } else {
      setForm(EMPTY)
    }
  }, [open, isEdit, initialValue])

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((p) => ({ ...p, [k]: v }))

  const canSave = !loading

  const handleClose = () => {
    if (loading) return
    onClose()
  }

  const buildBody = () => ({
    cvbb_type: form.cvbb_type.trim() || null,
    cvbb_vn: form.cvbb_vn.trim() || null,
    cvbb_eng: form.cvbb_eng.trim() || null,
    note: form.note.trim() || null,
  })

  const handleSave = async () => {
    try {
      if (isEdit && initialValue) {
        await updateCvbbType({ id: initialValue.cvbb_id, body: buildBody() }).unwrap()
        snack.success('CVBB type updated')
      } else {
        await createCvbbType(buildBody()).unwrap()
        snack.success('CVBB type created')
      }
      onSaved?.()
      onClose()
    } catch (e) {
      snack.error(getErrorMessage(e, isEdit ? 'Failed to update' : 'Failed to create'))
    }
  }

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (loading) return
        if (reason === 'backdropClick') return
        handleClose()
      }}
      maxWidth="sm"
      fullWidth
      scroll="paper"
    >
      <DialogTitle sx={dialogTitleSx}>
        <Typography component="span" noWrap sx={dialogTitleTextSx}>
          {isEdit ? 'Edit CVBB Type' : 'New CVBB Type'}
          {isEdit && initialValue ? (
            <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
              #{initialValue.cvbb_id}
            </Typography>
          ) : null}
        </Typography>
        <Stack direction="row" spacing={0.5} sx={dialogTitleActionsSx}>
          <Button
            type="button"
            variant="contained"
            size="small"
            color="success"
            onClick={() => void handleSave()}
            disabled={!canSave}
            sx={dialogHeaderBtnSx}
          >
            {loading ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button type="button" variant="outlined" size="small" onClick={handleClose} disabled={loading} sx={dialogHeaderBtnSx}>
            Cancel
          </Button>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent dividers sx={compactDialogContentSx}>
        <Grid container spacing={1.5} sx={{ mt: 0 }}>
          {isEdit && initialValue ? (
            <Grid item xs={12}>
              <TextField
                label="CVBB Type ID"
                size="small"
                fullWidth
                value={String(initialValue.cvbb_id)}
                InputLabelProps={{ shrink: true }}
                InputProps={{ readOnly: true }}
                inputProps={{ tabIndex: -1 }}
                helperText="CVBB Type ID is auto-generated."
                sx={roSx}
              />
            </Grid>
          ) : null}
          <Grid item xs={12}>
            <TextField
              label="CVBB Type"
              size="small"
              fullWidth
              value={form.cvbb_type}
              onChange={(e) => set('cvbb_type', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: 1 }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Description (VN)"
              size="small"
              fullWidth
              value={form.cvbb_vn}
              onChange={(e) => set('cvbb_vn', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: 2 }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Description (ENG)"
              size="small"
              fullWidth
              value={form.cvbb_eng}
              onChange={(e) => set('cvbb_eng', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: 3 }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Note"
              size="small"
              fullWidth
              multiline
              minRows={2}
              value={form.note}
              onChange={(e) => set('note', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: 4 }}
            />
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  )
}
